/**
 * Plan Mode Manager
 * Claude Code-style plan mode for analyzing and planning before execution
 */

import fs from 'fs';
import path from 'path';
import kleur from 'kleur';

export interface PlanStep {
  step: number;
  action: string;
  description: string;
  files?: string[];
  reasoning?: string;
}

export interface Plan {
  id: string;
  title: string;
  description: string;
  steps: PlanStep[];
  created: string;
  status: 'draft' | 'approved' | 'executing' | 'completed';
  estimatedFiles?: string[];
  risks?: string[];
}

// Tools allowed in plan mode (read-only + research)
export const PLAN_MODE_ALLOWED_TOOLS = [
  // File reading
  'read_file',
  'list_files',
  'find_files',
  'search_files',
  'glob',

  // Search
  'grep',
  'ripgrep',
  'search',

  // Git read operations
  'git_status',
  'git_diff',
  'git_log',
  'git_branch',
  'git_status_enhanced',

  // Research
  'web_search',
  'web_fetch',

  // Todo management (planning)
  'create_todo',
  'update_todo',
  'list_todos',
  'delete_todo',

  // System info
  'env',
  'which',
  'ps',

  // Plan mode tools
  'suggest_plan_mode',
  'check_plan_mode',
];

// Tools blocked in plan mode (write/modify operations)
export const PLAN_MODE_BLOCKED_TOOLS = [
  'write_file',
  'edit_file',
  'multi_edit',
  'append_file',
  'move_file',
  'delete_file',
  'create_directory',
  'bash',
  'execute_command',
  'git_add',
  'git_commit',
  'git_push',
  'git_checkout',
  'git_merge',
  'git_rebase',
  'git_stash',
];

export class PlanModeManager {
  private enabled: boolean = false;
  private currentPlan: Plan | null = null;
  private plansDir: string;
  private sessionId: string;

  constructor(workingDirectory: string, sessionId: string) {
    this.plansDir = path.join(workingDirectory, '.metis', 'plans');
    this.sessionId = sessionId;
    this.ensurePlansDirectory();
  }

  private ensurePlansDirectory(): void {
    try {
      if (!fs.existsSync(this.plansDir)) {
        fs.mkdirSync(this.plansDir, { recursive: true });
      }
    } catch (error) {
      // Ignore errors
    }
  }

  // Check if plan mode is enabled
  isEnabled(): boolean {
    return this.enabled;
  }

  // Enable plan mode
  enable(): void {
    this.enabled = true;
    this.currentPlan = null;
  }

  // Disable plan mode
  disable(): void {
    this.enabled = false;
  }

  // Toggle plan mode
  toggle(): boolean {
    this.enabled = !this.enabled;
    if (!this.enabled) {
      this.currentPlan = null;
    }
    return this.enabled;
  }

  // Check if a tool is allowed in plan mode
  isToolAllowed(toolName: string): boolean {
    if (!this.enabled) return true;

    // Check if tool is in allowed list
    if (PLAN_MODE_ALLOWED_TOOLS.includes(toolName)) {
      return true;
    }

    // Check if tool is explicitly blocked
    if (PLAN_MODE_BLOCKED_TOOLS.includes(toolName)) {
      return false;
    }

    // Default: block unknown tools in plan mode for safety
    return false;
  }

  // Get blocked message for a tool
  getBlockedMessage(toolName: string): string {
    return `Tool '${toolName}' is not available in Plan Mode. ` +
      `Plan Mode only allows read-only operations for research and analysis. ` +
      `Exit Plan Mode with /execute to perform this action.`;
  }

  // Create a new plan
  createPlan(title: string, description: string): Plan {
    const plan: Plan = {
      id: `plan-${Date.now()}`,
      title,
      description,
      steps: [],
      created: new Date().toISOString(),
      status: 'draft',
    };

    this.currentPlan = plan;
    return plan;
  }

  // Add step to current plan
  addStep(action: string, description: string, files?: string[], reasoning?: string): void {
    if (!this.currentPlan) {
      this.createPlan('Untitled Plan', 'Auto-created plan');
    }

    const step: PlanStep = {
      step: (this.currentPlan!.steps.length || 0) + 1,
      action,
      description,
      files,
      reasoning,
    };

    this.currentPlan!.steps.push(step);
  }

  // Get current plan
  getCurrentPlan(): Plan | null {
    return this.currentPlan;
  }

  // Save plan to file
  savePlan(plan?: Plan): string {
    const p = plan || this.currentPlan;
    if (!p) {
      throw new Error('No plan to save');
    }

    const planPath = path.join(this.plansDir, `${p.id}.json`);
    fs.writeFileSync(planPath, JSON.stringify(p, null, 2), 'utf8');

    // Also save as markdown for readability
    const mdPath = path.join(this.plansDir, `${p.id}.md`);
    fs.writeFileSync(mdPath, this.planToMarkdown(p), 'utf8');

    return planPath;
  }

  // Load plan from file
  loadPlan(planId: string): Plan | null {
    try {
      const planPath = path.join(this.plansDir, `${planId}.json`);
      if (fs.existsSync(planPath)) {
        const data = fs.readFileSync(planPath, 'utf8');
        this.currentPlan = JSON.parse(data);
        return this.currentPlan;
      }
    } catch (error) {
      // Ignore errors
    }
    return null;
  }

  // List all plans
  listPlans(): Array<{ id: string; title: string; created: string; status: string }> {
    try {
      const files = fs.readdirSync(this.plansDir)
        .filter(f => f.endsWith('.json'));

      return files.map(f => {
        try {
          const data = JSON.parse(fs.readFileSync(path.join(this.plansDir, f), 'utf8'));
          return {
            id: data.id,
            title: data.title,
            created: data.created,
            status: data.status,
          };
        } catch {
          return null;
        }
      }).filter(Boolean) as any[];
    } catch {
      return [];
    }
  }

  // Approve current plan for execution
  approvePlan(): void {
    if (this.currentPlan) {
      this.currentPlan.status = 'approved';
      this.savePlan();
    }
  }

  // Convert plan to markdown
  planToMarkdown(plan: Plan): string {
    const lines: string[] = [
      `# ${plan.title}`,
      '',
      plan.description,
      '',
      `**Status:** ${plan.status}`,
      `**Created:** ${new Date(plan.created).toLocaleString()}`,
      '',
      '## Steps',
      '',
    ];

    for (const step of plan.steps) {
      lines.push(`### ${step.step}. ${step.action}`);
      lines.push('');
      lines.push(step.description);

      if (step.files && step.files.length > 0) {
        lines.push('');
        lines.push('**Files:**');
        for (const file of step.files) {
          lines.push(`- ${file}`);
        }
      }

      if (step.reasoning) {
        lines.push('');
        lines.push(`**Reasoning:** ${step.reasoning}`);
      }

      lines.push('');
    }

    if (plan.risks && plan.risks.length > 0) {
      lines.push('## Risks');
      lines.push('');
      for (const risk of plan.risks) {
        lines.push(`- ${risk}`);
      }
      lines.push('');
    }

    return lines.join('\n');
  }

  // Format plan for display
  formatPlanDisplay(plan?: Plan): string {
    const p = plan || this.currentPlan;
    if (!p) return 'No plan available';

    const lines: string[] = [
      kleur.cyan(`Plan: ${p.title}`),
      kleur.gray('-'.repeat(50)),
      '',
      p.description,
      '',
      kleur.white('Steps:'),
    ];

    for (const step of p.steps) {
      lines.push(`  ${kleur.yellow(step.step + '.')} ${step.action}`);
      lines.push(`     ${kleur.gray(step.description)}`);
      if (step.files && step.files.length > 0) {
        lines.push(`     ${kleur.gray('Files: ' + step.files.join(', '))}`);
      }
    }

    lines.push('');
    lines.push(kleur.gray(`Status: ${p.status}`));

    return lines.join('\n');
  }

  // Get plan mode system prompt addition
  getPlanModePrompt(): string {
    if (!this.enabled) return '';

    return `
## PLAN MODE ACTIVE

You are currently in **Plan Mode**. In this mode:

1. **DO NOT** execute any code changes, file modifications, or destructive operations
2. **DO** analyze the codebase, research the problem, and create a detailed plan
3. **DO** use read-only tools: read_file, list_files, grep, git_status, git_diff, web_search
4. **DO NOT** use write tools: write_file, edit_file, bash, git_commit, etc.

### Your Task in Plan Mode:

1. **Understand** the user's request thoroughly
2. **Research** the codebase to understand existing patterns and architecture
3. **Identify** all files that would need to be modified
4. **Create a detailed plan** with numbered steps
5. **Explain your reasoning** for each step
6. **Identify potential risks** or considerations

### Plan Format:

Present your plan as:

**Plan: [Title]**

**Overview:** Brief description of what will be accomplished

**Steps:**
1. **[Action]** - Description of what will be done
   - Files: list of files affected
   - Reasoning: why this step is needed

2. **[Action]** - Description
   ...

**Risks/Considerations:**
- List any potential issues or things to watch out for

**Estimated Impact:**
- Files to modify: X
- New files: Y
- Complexity: Low/Medium/High

When the user is ready to execute, they will exit Plan Mode with /execute.
`;
  }
}

// Global instance
let globalPlanModeManager: PlanModeManager | null = null;

export function getPlanModeManager(workingDirectory?: string, sessionId?: string): PlanModeManager {
  if (!globalPlanModeManager) {
    globalPlanModeManager = new PlanModeManager(
      workingDirectory || process.cwd(),
      sessionId || `session-${Date.now()}`
    );
  }
  return globalPlanModeManager;
}
